import { AbstractEntity } from '@app/shared/abstract/abstract-entity';
import { VenueModel } from '@app/venues/shared/venue.model';
import { NoteModel } from '@app/events/shared/note.model';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { ReminderModel } from '@app/events/shared/reminder.model';
import { LifecycleStage, PortalCapabilities, ReservationFeeStatus } from '@app/portal/portal-state.model';

// TVG-119: Active Opportunity sub-status enum, mirrored from the PHP Event model.
export type EventSubStatus = 'event_created' | 'in_conversation';

// TVG-121: booked viewing details for the customer portal Event Details preview.
export interface ViewingDetailsModel {
  show_round_at: string | null;
  venue: {
    name: string | null;
    address: string | null;
    maps_url?: string | null;
  } | null;
}

export interface EventPortalContact {
  id: number;
  name: string;
  email: string | null;
}

export interface EventModel extends AbstractEntity {
  [key: string]: any;

  id: number;
  venue_id: number;
  name: string;
  reference: string;
  description: string;
  status: 'Opportunity' | 'Provisional' | 'Confirmed' | 'Cancelled' | 'Closed' | 'Lost';
  /** TVG-119: Active Opportunity sub-status. Present only while crm-sales-flow is on. */
  sub_status?: EventSubStatus | null;
  confirmed_at?: Date;
  cancelled_at?: Date | string;
  /** TVG-124: timestamp set when the event was marked as Lost. */
  lost_at?: Date | string | null;
  /** TVG-124: optional reason captured at the point of Mark as Lost. */
  lost_reason?: string | null;
  category:
    | 'Wedding'
    | 'Corporate Event'
    | 'Christmas Party'
    | 'Private Event'
    | 'Tasting Event'
    | 'Open Day'
    | 'Sales Day'
  priority: number;
  priority_name: 'Low' | 'Medium' | 'High' | 'Critical';
  start_date: Date;
  end_date: Date;
  arrival: Date;
  departure: Date;
  // TVG-121: nullable because cancel/no-show clears the date and the model
  // is reused for events without a booked viewing.
  show_round: string | Date | null;
  bride: string;
  groom: string;
  brides_parents: string;
  grooms_parents: string;
  maid_of_honour: string;
  bridesmaids: string;
  flower_girls: string;
  flower_flower_girls_walking: boolean;
  paige_boys: string;
  paige_paige_boys_walking: boolean;
  best_men: string;
  groomsmen: string;
  bride_escort: string;
  bridal_party_entrance_order: string;
  menu_code_string: string;
  special_offers_string: string;
  sales_manager_id: number;
  event_manager_id: number;
  show_on_board: boolean;
  minimum_spend_override: number;
  hire_price_override: number;
  reservation_fee: number;
  deposit: number;
  payment_sync_enabled: boolean;
  template_id: number | null;
  venue_quote_id?: number | null;
  day_guests?: number | null;
  evening_guests?: number | null;
  has_guest_allergies: boolean;
  guest_allergies: string;
  supplier_notes?: string | null;
  rules_conduct_submitted_at?: string | null;
  rules_conduct_submitted_by_id?: number | null;
  rules_conduct_submitted_by?: string | null;

  has_accommodation_tab?: boolean;

  /** TVG-110: present on the customer portal payload when the crm-sales-flow toggle is enabled. */
  lifecycle_stage?: LifecycleStage;
  /** TVG-110: present on the customer portal payload when the crm-sales-flow toggle is enabled. */
  capabilities?: PortalCapabilities;
  /** TVG-116: Stage 5 (Pencil) reservation-fee status. Null when not Pencil or when no reservation-fee row exists. */
  reservation_fee_status?: ReservationFeeStatus | null;
  /** TVG-113: latest prospect-side activity timestamp across all contacts. Present when crm-sales-flow is on. */
  last_prospect_activity_at?: string | null;
  /** TVG-113: latest sales-side touch timestamp across all contacts. Present when crm-sales-flow is on. */
  last_sales_touch_at?: string | null;

  notes: NoteModel[];
  reminders: ReminderModel[];
  products: EventProductItemModel[];
  timeline_items: EventTimelineItemModel[];

  contacts_ids: number[];

  // Planner information
  created_by_name?: string;
  sales_manager_name?: string;
  event_manager_name?: string;

  /** TVG-110: emitted by EventOverviewResource for the customer portal contact card. */
  sales_manager?: EventPortalContact | null;
  event_manager?: EventPortalContact | null;

  /** TVG-121: booked viewing details for the customer portal Event Details preview. */
  viewing_details?: ViewingDetailsModel | null;

  // media URLs
  seating_plans: { sm: string; md: string; lg: string; xl: string }[];
  seating_plans_1_sm: string;

  venue?: VenueModel;
}


export class EventModelUtils {
  static getCategoryColor(category: EventModel['category']): string {
    switch (category) {
      case 'Wedding':
        return 'warning';
      case 'Corporate Event':
        return 'primary';
      case 'Christmas Party':
        return 'success';
      case 'Private Event':
        return 'secondary';
      case 'Tasting Event':
        return 'info';
      case 'Open Day':
        return 'light';
      case 'Sales Day':
        return 'dark';
      default:
        return 'light';
    }
  }

  static getStatusColor(status: EventModel['status']): string {
    switch (status) {
      case 'Opportunity':
        return 'warning';
      case 'Provisional':
        return 'primary';
      case 'Confirmed':
        return 'success';
      case 'Cancelled':
        return 'error';
      case 'Closed':
        return 'light';
      case 'Lost':
        return 'light';
      default:
        return 'light';
    }
  }

  static getPriorityColor(priority: EventModel['priority_name']): string {
    switch (priority) {
      case 'Low':
        return 'success';
      case 'Medium':
        return 'warning';
      case 'High':
        return 'error';
      case 'Critical':
        return 'error';
      default:
        return 'light';
    }
  }

  /**
   * TVG-113: Returns the freshness colour for the prospect activity dot, or null when
   * no dot should render. Rules:
   *   green — activity in the last 7 days
   *   amber — activity in the last 7–30 days
   *   grey  — activity 30+ days ago, or never
   * The dot is suppressed on Confirmed / Cancelled / Closed cards: post-Confirmation
   * the operational signal is the wedding date countdown, and lost cards carry no
   * freshness meaning.
   */
  static getFreshnessColor(event: EventModel, now: Date = new Date()): 'green' | 'amber' | 'grey' | null {
    if (event.status === 'Confirmed' || event.status === 'Cancelled' || event.status === 'Closed' || event.status === 'Lost') {
      return null;
    }

    const ts = event.last_prospect_activity_at;
    if (!ts) {
      return 'grey';
    }

    const activityAt = new Date(ts);
    if (Number.isNaN(activityAt.getTime())) {
      return 'grey';
    }

    const dayMs = 24 * 60 * 60 * 1000;
    const ageDays = (now.getTime() - activityAt.getTime()) / dayMs;

    if (ageDays < 7) {
      return 'green';
    }

    if (ageDays < 30) {
      return 'amber';
    }

    return 'grey';
  }

  /**
   * TVG-113: Returns whether the last_sales_touch_at timestamp should render on the
   * given card. Per the lifecycle doc this appears on Active Opportunity and Viewing
   * Booked only, both of which carry status='Opportunity' in the data model.
   */
  static showsLastSalesTouch(event: EventModel): boolean {
    return event.status === 'Opportunity' && !!event.last_sales_touch_at;
  }

  /**
   * TVG-119: Human label for an Active Opportunity sub-status, used on the
   * detail-page toggle and the board column badge / filter pills.
   */
  static getSubStatusLabel(subStatus: EventSubStatus | null | undefined): string {
    switch (subStatus) {
      case 'event_created':
        return 'Event Created';
      case 'in_conversation':
        return 'In Conversation';
      default:
        return '';
    }
  }

  /**
   * TVG-119: Badge palette mapping for the sub-status pill. Uses existing
   * app-badge tokens: `light` (neutral grey) for Event Created, `warning`
   * (amber) for In Conversation.
   */
  static getSubStatusColor(subStatus: EventSubStatus | null | undefined): string {
    switch (subStatus) {
      case 'event_created':
        return 'light';
      case 'in_conversation':
        return 'warning';
      default:
        return 'light';
    }
  }
}
